import { useContext, useState, useRef, useEffect } from "react"
import axios from "axios"
import { Calendar } from "primereact/calendar"
import { Dropdown } from "primereact/dropdown"
import { Button } from "primereact/button"
import { Toast } from "primereact/toast"
import { EngagementChannels } from "../Types/Dropdowns"
import EditorField from "../Utils/EditorField.js"
import styles from "../../../styles/Participant-Employment.module.css"
import { UserContext } from "../../../context/userContext"
import {
  Actions,
  showError,
  formatDateAndDuration,
  convertToLocalDateTime,
  requieredFieldIsMissing,
  renderRequireFieldErrorMessage,
  addOneMinute,
  substractOneMinute,
  formatDate,
} from "../Utils/Utils"
import { employmentApiUrl } from "../../../config/microservicesUrls"

const api_url = employmentApiUrl()

export default function JobSearchPlan(props) {
  const toast = useRef(null)

  const userCtx = useContext(UserContext)

  const [loading, setLoading] = useState(false)

  const [formErrors, setFormErrors] = useState(null)

  const defaultData = {
    engagementDate: formatDate(new Date()),
    engagementChannel: EngagementChannels[0],
    startTime: new Date(),
    endTime: addOneMinute(new Date()),
    jobSearchGoals: "",
    jobRelatedStrengths: "",
  }

  const [data, setData] = useState(defaultData)

  useEffect(() => {
    if (props.jobSearchPlan && props.jobSearchPlan !== null) {
      const engagementDate = convertToLocalDateTime(
        props.jobSearchPlan.engagementDate
      )
      const startTime = convertToLocalDateTime(props.jobSearchPlan.startTime)
      const endTime = convertToLocalDateTime(props.jobSearchPlan.endTime)

      const { formattedDate, formattedStartTime, formattedEndTime } =
        formatDateAndDuration(engagementDate, startTime, endTime)

      setData({
        id: props.jobSearchPlan.id,
        engagementDate: formattedDate,
        engagementChannel:
          EngagementChannels[props.jobSearchPlan.engagementChannel],
        startTime: formattedStartTime,
        endTime: formattedEndTime,
        jobSearchGoals: props.jobSearchPlan.jobSearchGoals,
        jobRelatedStrengths: props.jobSearchPlan.jobRelatedStrengths,
        searchGoals: props.jobSearchPlan.searchGoals,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clearForm = () => {
    setData(defaultData)
    setFormErrors(null)
  }

  const handleSave = async () => {
    setLoading(true)
    const engagementChannel = EngagementChannels.indexOf(data.engagementChannel)
    const { formattedDate, formattedStartTime, formattedEndTime } =
      formatDateAndDuration(data.engagementDate, data.startTime, data.endTime)

    if (props.action === Actions.Add) {
      const createdData = {
        createdBy: userCtx.session.user.staff_id,
        ...data,
        engagementChannel,
        engagementDate: formattedDate,
        startTime: formattedStartTime,
        endTime: formattedEndTime,
        referralId:userCtx.currentActiveReferral,
        organizationId: userCtx.session.user.organization_id,
        participantId: userCtx.current_participant_id,
      }
      axios
        .post(`${api_url}/jobSearchPlan`, createdData, {
          headers: { Authorization: `Bearer ${userCtx.tokenEmployment}` },
        })
        .then(function (response) {
          clearForm()
          props.updateData(response.data)
          setLoading(false)
          props.closeForm()
        })
        .catch(function (response) {
          const errors = response["response"]["data"]["errors"]
          setLoading(false)
          setFormErrors(errors)
          showError(toast, "Not able to create job search plan", errors)
        })
    } else {
      const updatedData = {
        updatedBy: userCtx.session.user.staff_id,
        ...data,
        engagementChannel,
        engagementDate: formattedDate,
        startTime: formattedStartTime,
        endTime: formattedEndTime,
      }
      axios
        .put(`${api_url}/jobSearchPlan`, updatedData, {
          headers: { Authorization: `Bearer ${userCtx.tokenEmployment}` },
        })
        .then(function (response) {
          clearForm()
          props.updateData(response.data)
          setLoading(false)
          props.closeForm()
        })
        .catch(function (response) {
          const errors = response["response"]["data"]["errors"]
          setLoading(false)
          setFormErrors(errors)
          showError(toast, "Not able to update job search plan", errors)
        })
    }
  }

  const renderEngagementDateErrorMessage = (field) => {
    if (requieredFieldIsMissing(field))
      return <small className="p-error">Engagement date is required</small>
    else if (props.minTargetDate && field > props.minTargetDate)
      return (
        <small className="p-error">
          The Engagement Date cannot be greater than a Search Goal Target Date
        </small>
      )
    return null
  }

  const renderFormErrors = () => {
    if (formErrors !== null) {
      return (
        <ul className={styles.errors_list}>
          {formErrors.map((error, index) => {
            return (
              <li key={index}>
                <small className="p-error">{error}</small>
              </li>
            )
          })}
        </ul>
      )
    }
    return null
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div className="main-content">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h1 className={styles.view_title}>
            {props.action === Actions.Add
              ? "Add job search plan"
              : props.action === Actions.Edit
                ? "Edit job search plan"
                : "Job search plan"}
          </h1>
          <Button
            className={styles.rounded_btn}
            icon="pi pi-times"
            type="button"
            disabled={loading}
            onClick={() => {
              clearForm()
              props.closeForm()
            }}
          />
        </div>
        <div className={"card flex flex-wrap p-fluid " + styles.wrapper}>
          <h3 className={styles.view_subtitle}>Job search plan</h3>
          <hr className={styles.line}></hr>
          <div className={styles.flex_card}>
            <div className={styles.field}>
              <label className={`${styles.label} ${styles.required}`}>
                Engagement date
              </label>
              <Calendar
                name="Engagement date"
                value={data.engagementDate}
                onChange={(e) => {
                  if (data.engagementDate < e.value) {
                    props.setSearchGoalDefaultTargetDate(e.value)
                  }
                  setData({ ...data, engagementDate: e.value })
                }}
                panelClassName="custom_calendar_panel"
                dateFormat="mm/dd/yy"
                showIcon
                className={`${styles.calendar} ${requieredFieldIsMissing(data.engagementDate)
                  ? styles.invalid
                  : ""
                  }`}
                disabled={props.action === Actions.View}
              />
              {renderEngagementDateErrorMessage(data.engagementDate)}
            </div>
            <div className={styles.field} style={{ width: "200px" }}>
              <label className={`${styles.label} ${styles.required}`}>
                Engagement channel
              </label>
              <Dropdown
                style={{ height: "32px", alignItems: "center" }}
                value={data.engagementChannel}
                options={EngagementChannels}
                panelClassName="custom_dropdown_panel"
                onChange={(e) =>
                  setData({ ...data, engagementChannel: e.value })
                }
                placeholder="Select a channel"
                disabled={props.action === Actions.View}
              />
            </div>
            <div className={styles.field}>
              <label className={`${styles.label} ${styles.required}`}>
                Start time
              </label>
              <Calendar
                name="Start time"
                value={data.startTime}
                timeOnly
                panelClassName="custom_calendar_panel"
                onChange={(e) => setData({ ...data, startTime: e.value })}
                showIcon
                icon="pi pi-clock"
                className={`${styles.calendar} ${requieredFieldIsMissing(data.startTime) ? styles.invalid : ""
                  }`}
                hourFormat="12"
                maxDate={substractOneMinute(data.endTime)}
                disabled={props.action === Actions.View}
              />
              {renderRequireFieldErrorMessage(
                data.startTime,
                "Start time is required"
              )}
            </div>
            <div className={styles.field}>
              <label className={`${styles.label} ${styles.required}`}>
                End time
              </label>
              <Calendar
                name="End time"
                value={data.endTime}
                timeOnly
                panelClassName="custom_calendar_panel"
                hourFormat="12"
                onChange={(e) => setData({ ...data, endTime: e.value })}
                showIcon
                icon="pi pi-clock"
                className={`${styles.calendar} ${requieredFieldIsMissing(data.endTime) ? styles.invalid : ""
                  }`}
                minDate={addOneMinute(data.startTime)}
                disabled={props.action === Actions.View}
              />
              {renderRequireFieldErrorMessage(
                data.endTime,
                "End time is required"
              )}
            </div>
          </div>

          <EditorField
            value={data.jobSearchGoals}
            onTextChange={(e) => setData({ ...data, jobSearchGoals: e })}
            label="What are the participant's job search goals?"
            disabled={props.action === Actions.View}
          />

          <EditorField
            value={data.jobRelatedStrengths}
            onTextChange={(e) => setData({ ...data, jobRelatedStrengths: e })}
            label="What are the participant's job related strengths?"
            disabled={props.action === Actions.View}
          />

          <br />
          <div
            style={{ display: "flex", gap: "20px", justifyContent: "flex-end" }}
          >
            <Button
              className={styles.cancel_button}
              type="button"
              disabled={loading}
              onClick={() => {
                clearForm()
                props.closeForm()
              }}
            >
              {props.action === Actions.View ? "Back" : "Cancel"}
            </Button>
            {props.action !== Actions.View && (
              <Button
                className={styles.button}
                type="submit"
                disabled={
                  requieredFieldIsMissing(data.engagementDate) ||
                  requieredFieldIsMissing(data.startTime) ||
                  requieredFieldIsMissing(data.endTime) ||
                  requieredFieldIsMissing(data.engagementChannel) ||
                  loading
                }
                icon={loading ? "pi pi-spin pi-spinner" : ""}
                iconPos="right"
                label={`${props.action === Actions.Add ? "Create" : "Update"
                  } job search plan`}
                onClick={() => handleSave()}
              />
            )}
          </div>
          {renderFormErrors()}
        </div>
      </div>
    </>
  )
}
